// Aplica y persiste la apariencia. El primer pintado ya lo hizo el script
// inline de noble/layout/head.php (window.__fdPrefs); aquí se reaccionan los
// controles del panel del perfil ([data-fd-pref], Tarea B6) y el cambio del SO.
// Sin panel en la página (interruptor FD_APARIENCIA_ACTIVA apagado, kioskos,
// acceso) no hace nada hasta que alguien llame aplicar() o guardar().
// Expuesto como window.FDApariencia (app.js); tras cada aplicación dispara
// `fd:apariencia` en document con detail = preferencias (lo usa el tablero).
import { normalizar, fusionar, aCookie, atributos } from './lib/apariencia-prefs.js';

const COOKIE = 'fd_ui';
const UN_ANIO = 31536000; // 365 días, como Apariencia_prefs::COOKIE_DIAS
// Honesto (revisión final, Menor 3): la sesión manda en la próxima navegación
// (head.php la reescribe), así que un guardado fallido NO "se conserva" en este
// navegador; solo queda aplicado en la página actual hasta que se recargue.
const AVISO = 'Tu elección se aplicó en esta página; aún no se guardó para la próxima vez.';
let prefs = normalizar(window.__fdPrefs);
const enlazados = new WeakSet();
let mqlSO = null; // referencia viva: el oyente del SO no debe quedar huérfano
let conTeclado = false;

function mq() { return window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null; }
function sistemaOscuro() { const m = mq(); return !!(m && m.matches); }

// Radiogroup (WAI-ARIA): aria-checked en la opción vigente y tabindex itinerante,
// solo la marcada entra al Tab; las flechas recorren el grupo (ver init).
function sincronizarControles() {
  document.querySelectorAll('[data-fd-pref]').forEach(function (grupo) {
    const clave = grupo.getAttribute('data-fd-pref');
    const radios = Array.prototype.slice.call(grupo.querySelectorAll('[role="radio"]'));
    let alguno = false;
    radios.forEach(function (b) {
      const marcado = b.getAttribute('data-value') === prefs[clave];
      alguno = alguno || marcado;
      b.setAttribute('aria-checked', marcado ? 'true' : 'false');
      b.tabIndex = marcado ? 0 : -1;
    });
    if (!alguno && radios.length) radios[0].tabIndex = 0;
  });
}

export function actual() { return Object.assign({}, prefs); }

export function aplicar(parcial) {
  prefs = fusionar(prefs, parcial);
  const at = atributos(prefs, sistemaOscuro());
  Object.keys(at).forEach(function (k) { document.documentElement.setAttribute(k, at[k]); });
  // Mismo formato que Apariencia_prefs::header_cookie() (sin url-encode: el head no decodifica).
  document.cookie = COOKIE + '=' + aCookie(prefs) + '; max-age=' + UN_ANIO + '; path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
  window.__fdPrefs = actual();
  sincronizarControles();
  document.dispatchEvent(new CustomEvent('fd:apariencia', { detail: actual() }));
}

export function guardar(parcial) {
  aplicar(parcial);
  const FD = window.FD;
  if (!FD || typeof FD.ajax !== 'function') return;
  const avisar = function (msg) {
    if (typeof FD.toast === 'function') FD.toast('warning', msg, 'Apariencia');
  };
  FD.ajax({ url: FD.baseUrl('v2/apariencia/guardar_ajax'), data: actual() })
    .done(function (r) {
      // El endpoint contesta 200 también en los errores de negocio: manda `code`.
      if (r && String(r.code) === '200') return;
      // 5003 = interruptor FD_APARIENCIA_ACTIVA apagado (el servidor lo explica).
      avisar(r && String(r.code) === '5003' && r.msg ? r.msg : AVISO);
    })
    .fail(function (x) {
      if (x && (x.status === 419 || x.status === 403)) return; // ya los maneja FD.ajax
      avisar(AVISO);
    });
}

// Flechas de los radiogroups: mueven el foco y aplican, con vuelta al inicio.
// Van en captura sobre window porque el panel vive dentro del dropdown del
// perfil: el manejador delegado de teclado de Bootstrap corre en captura sobre
// document, antes que cualquier oyente del botón, y con ↑/↓ se quedaba con el
// evento y mandaba el foco a los .dropdown-item (↑ caía en "Cerrar Sesión").
function alTeclear(e) {
  // Alt/Cmd+← y Ctrl/Alt+← conservan su atajo del navegador (Atrás/Adelante):
  // no se interceptan aunque el foco esté en un radio del panel (Menor 6).
  if (e.altKey || e.ctrlKey || e.metaKey) return;
  const d = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
  const b = e.target;
  const grupo = d && b && typeof b.closest === 'function' ? b.closest('[data-fd-pref]') : null;
  if (!grupo || !enlazados.has(grupo) || b.getAttribute('role') !== 'radio') return;
  e.preventDefault();
  e.stopPropagation();
  const radios = Array.prototype.slice.call(grupo.querySelectorAll('[role="radio"]'));
  const n = radios[(radios.indexOf(b) + d + radios.length) % radios.length];
  n.focus(); n.click();
}

export function init() {
  document.querySelectorAll('[data-fd-pref]').forEach(function (grupo) {
    if (enlazados.has(grupo)) return;
    enlazados.add(grupo);
    const clave = grupo.getAttribute('data-fd-pref');
    const radios = Array.prototype.slice.call(grupo.querySelectorAll('[role="radio"]'));
    radios.forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        const valor = b.getAttribute('data-value');
        if (valor === prefs[clave]) return; // ya vigente: nada que guardar
        const cambio = {}; cambio[clave] = valor;
        guardar(cambio);
      });
    });
  });
  if (!conTeclado) {
    conTeclado = true;
    window.addEventListener('keydown', alTeclear, true);
  }
  const m = mq();
  if (m && !mqlSO) {
    mqlSO = m;
    const alCambiarSO = function () { if (prefs.tema === 'sistema') aplicar({}); };
    if (m.addEventListener) m.addEventListener('change', alCambiarSO); else if (m.addListener) m.addListener(alCambiarSO);
  }
  sincronizarControles();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
